import type { ArtifactDetail } from '../api'

/**
 * Markdown-flavored plaintext suitable for paste into email, doc, slack,
 * or anywhere else. Header on top, content body in the middle, then
 * comments + redlines at the bottom when present. Renders cleanly as
 * either plain text or markdown.
 */
export function formatArtifactForCopy(a: ArtifactDetail): string {
  const lines: string[] = []
  lines.push(`# ${a.subject || '(no subject)'}`)
  lines.push('')
  lines.push(`Identifier: ${a.composite_id}`)
  lines.push(`Type: ${a.artifact_type}${a.mime_type ? ` (${a.mime_type})` : ''}`)
  if (a.from_email) {
    const fromLine = a.from_name ? `${a.from_name} <${a.from_email}>` : a.from_email
    lines.push(`From: ${fromLine}`)
  }
  if (a.to_emails?.length) lines.push(`To: ${a.to_emails.join(', ')}`)
  if (a.cc_emails?.length) lines.push(`Cc: ${a.cc_emails.join(', ')}`)
  if (a.source_date) lines.push(`Date: ${new Date(a.source_date).toLocaleString()}`)
  if (a.parent_artifact) lines.push(`Attached to: artifact #${a.parent_artifact}`)
  lines.push('')
  lines.push('---')
  lines.push('')

  if (a.cached_content) {
    lines.push(a.cached_content)
  } else if (a.cached_file_url) {
    lines.push(`[Binary content of type ${a.mime_type || 'unknown'} — download from ${a.cached_file_url}]`)
  } else {
    lines.push('[No cached content]')
  }

  if (a.comments?.length) {
    lines.push('')
    lines.push('---')
    lines.push('')
    lines.push(`## Comments (${a.comments.length})`)
    lines.push('')
    for (const cAny of a.comments) {
      const c = cAny as Record<string, unknown>
      const author = (c.author_name as string) || (c.author_email as string) || 'unknown'
      const text = (c.text as string) || (c.content as string) || ''
      const quoted = (c.quoted as string) || (c.quoted_content as string) || ''
      const resolved = !!c.resolved
      const created = c.created_time as string | undefined
      const dateStr = created ? new Date(created).toLocaleDateString() : ''
      lines.push(`- **${author}**${dateStr ? ` (${dateStr})` : ''}${resolved ? ' [resolved]' : ''}`)
      if (quoted) {
        lines.push(`  > ${quoted.slice(0, 300).replace(/\n/g, ' ')}`)
      }
      lines.push(`  ${text.replace(/\n/g, '\n  ')}`)
      const replies = (c.replies as Array<Record<string, unknown>> | undefined) ?? []
      for (const rAny of replies) {
        const r = rAny as Record<string, unknown>
        const ra = (r.author_name as string) || (r.author_email as string) || 'unknown'
        const rt = (r.text as string) || (r.content as string) || ''
        lines.push(`    - ${ra}: ${rt.replace(/\n/g, ' ')}`)
      }
      lines.push('')
    }
  }

  if (a.redlines?.length) {
    if (!a.comments?.length) {
      lines.push('')
      lines.push('---')
    }
    lines.push('')
    lines.push(`## Redlines (${a.redlines.length})`)
    lines.push('')
    for (const rAny of a.redlines) {
      const r = rAny as Record<string, unknown>
      const author = (r.author_name as string) || 'unknown'
      const action = ((r.action as string) || 'EDIT').toUpperCase()
      const before = (r.before as string) || (r.text as string) || ''
      const after = (r.after as string) || ''
      const ctx = (r.surrounding_context as string) || (r.paragraph_context as string) || ''
      const created = r.created_time as string | undefined
      const dateStr = created ? new Date(created).toLocaleDateString() : ''
      lines.push(`- **${action}** by ${author}${dateStr ? ` (${dateStr})` : ''}`)
      if (before) lines.push(`  - before: ${before.replace(/\n/g, ' ')}`)
      if (after) lines.push(`  - after: ${after.replace(/\n/g, ' ')}`)
      if (ctx) lines.push(`  - context: "${ctx.slice(0, 200).replace(/\n/g, ' ')}"`)
      lines.push('')
    }
  }

  return lines.join('\n')
}

/**
 * Copy formatted artifact text to clipboard with a textarea fallback for
 * older browsers / non-HTTPS contexts. Returns true on success.
 */
export async function copyArtifactToClipboard(a: ArtifactDetail): Promise<boolean> {
  const text = formatArtifactForCopy(a)
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      return true
    } catch {
      return false
    }
  }
}
